/**
 * Social publishing API client — compose, schedule, and organise social posts.
 * The drafting/scheduling is fully functional; connecting an account + publishing
 * is the credential-gated boundary the backend reports honestly via /social/readiness.
 */

import { apiClient } from "@/api/client";

export type SocialPlatform = "linkedin" | "x" | "facebook" | "instagram";
export type SocialPostStatus = "DRAFT" | "SCHEDULED" | "PUBLISHING" | "PUBLISHED" | "FAILED";

export interface SocialPost {
  id: string;
  org_id: string;
  body: string;
  platforms: SocialPlatform[];
  media_refs: unknown[];
  status: SocialPostStatus;
  scheduled_at: string | null;
  published_at: string | null;
  last_error: string | null;
  /** The platform's id for the published post (LinkedIn: the post URN). */
  external_post_id?: string | null;
  /** A link to the published post. */
  external_url?: string | null;
  publish_attempts?: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface SocialAccount {
  id: string;
  platform: SocialPlatform;
  display_name: string | null;
  status: "PENDING" | "CONNECTED" | "REVOKED" | "ERROR";
  connected_at: string | null;
  last_error?: string | null;
}

/** Why a platform can or cannot publish right now. */
export type SocialPlatformStatus = "connected" | "not connected" | "not configured";

export interface LinkedInConnectResponse {
  configured: boolean;
  authorize_url: string | null;
  message: string | null;
}

export interface SocialReadiness {
  platforms: Record<SocialPlatform, boolean>;
  any_connected: boolean;
  /** Whether FTL holds app credentials for the platform. */
  configured?: Record<SocialPlatform, boolean>;
  platform_status?: Record<SocialPlatform, SocialPlatformStatus>;
  note: string;
}

export interface CreateSocialPostInput {
  body: string;
  platforms: SocialPlatform[];
  scheduled_at?: string | null;
}

export const socialApi = {
  /** GET /social/platforms — the platforms the composer supports. */
  getPlatforms(): Promise<{ platforms: SocialPlatform[] }> {
    return apiClient.get("/social/platforms");
  },

  /** GET /social/readiness — which platforms are publishable (connected). */
  getReadiness(): Promise<SocialReadiness> {
    return apiClient.get("/social/readiness");
  },

  /** GET /social/accounts — the org's connected/pending platform accounts. */
  listAccounts(): Promise<{ count: number; accounts: SocialAccount[] }> {
    return apiClient.get("/social/accounts");
  },

  /** GET /social/linkedin/connect — the LinkedIn authorize URL, or "not configured". */
  connectLinkedIn(): Promise<LinkedInConnectResponse> {
    return apiClient.get("/social/linkedin/connect");
  },

  /** GET /social/posts — the org's posts (optional status filter). */
  listPosts(status?: SocialPostStatus): Promise<{ count: number; posts: SocialPost[] }> {
    return apiClient.get("/social/posts", status ? { status } : undefined);
  },

  /** POST /social/posts — draft (or schedule) a post. */
  createPost(input: CreateSocialPostInput): Promise<SocialPost> {
    return apiClient.post<SocialPost>("/social/posts", input);
  },

  /** PATCH /social/posts/{id} — edit a draft/scheduled post. */
  updatePost(id: string, input: Partial<CreateSocialPostInput> & { clear_schedule?: boolean }): Promise<SocialPost> {
    return apiClient.patch<SocialPost>(`/social/posts/${id}`, input);
  },

  /** DELETE /social/posts/{id}. */
  deletePost(id: string): Promise<{ deleted: boolean; id: string }> {
    return apiClient.delete(`/social/posts/${id}`);
  },
};
